import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

export default function ArmyBodyFatCalc() {
  const [tab, setTab] = useState(0);
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState(25);
  const [wtLb, setWtLb] = useState(160);
  const [wtKg, setWtKg] = useState(73);
  const [hIn, setHIn] = useState(70);
  const [waistIn, setWaistIn] = useState(35);
  const [neckIn, setNeckIn] = useState(16);
  const [hipIn, setHipIn] = useState(40);
  const [hCm, setHCm] = useState(178);
  const [waistCm, setWaistCm] = useState(89);
  const [neckCm, setNeckCm] = useState(41);
  const [hipCm, setHipCm] = useState(102);
  const clear = () => { setGender('male'); setAge(25); setWtLb(160); setWtKg(73); setHIn(70); setWaistIn(35); setNeckIn(16); setHipIn(40); setHCm(178); setWaistCm(89); setNeckCm(41); setHipCm(102); };

  const c = useMemo(() => {
    const h = tab === 0 ? +hIn||0 : (+hCm||0)/2.54;
    const waist = tab === 0 ? +waistIn||0 : (+waistCm||0)/2.54;
    const neck = tab === 0 ? +neckIn||0 : (+neckCm||0)/2.54;
    const hip = tab === 0 ? +hipIn||0 : (+hipCm||0)/2.54;
    const weight = tab === 0 ? +wtLb || 0 : (+wtKg || 0) * 2.20462;
    let bf;
    if (gender === 'male') bf = 86.010 * Math.log10(Math.max(1, waist - neck)) - 70.041 * Math.log10(Math.max(1, h)) + 36.76;
    else bf = 163.205 * Math.log10(Math.max(1, waist + hip - neck)) - 97.684 * Math.log10(Math.max(1, h)) - 78.387;
    const max = gender === 'male'
      ? (age <= 20 ? 20 : age <= 27 ? 22 : age <= 39 ? 24 : 26)
      : (age <= 20 ? 30 : age <= 27 ? 32 : age <= 39 ? 34 : 36);
    const fatMass = weight * bf / 100;
    const leanMass = weight - fatMass;
    return { bf, meets: bf <= max, max, fatMass, leanMass };
  }, [tab, gender, age, wtLb, wtKg, hIn, waistIn, neckIn, hipIn, hCm, waistCm, neckCm, hipCm]);

  return (
    <CalcShell look="cnet" title="Army Body Fat Calculator" subtitle="Body-fat percentage using the U.S. Army tape-measure method (AR 600-9).">
      <CalcNetPanel slug="army" activeTab={tab} onTabChange={setTab} onClear={clear} right={
        <div>
          <div className="text-lg text-slate-800">Body-fat percentage</div>
          <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="army-bf">{c.bf.toFixed(1)}%</div>
          <div className="text-sm text-slate-700 mt-1">Fat Mass: <b>{c.fatMass.toFixed(1)} lbs</b> · Lean Mass: <b>{c.leanMass.toFixed(1)} lbs</b></div>
          <div className={`p-3 rounded text-sm mt-3 ${c.meets?'bg-green-50 text-green-800':'bg-red-50 text-red-800'}`}>
            Army max for your age/gender: <b>{c.max}%</b> — {c.meets ? '✓ Within standard' : '✗ Exceeds standard'}
          </div>
        </div>
      }>
        <Row label="Gender"><RadioGroup name="army-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]}/></Row>
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="army-age"/></Row>
        {tab === 0 ? <>
          <Row label="Height"><UnitInput value={hIn} onChange={setHIn} unit="inches" testid="army-h"/></Row>
          <Row label="Weight"><UnitInput value={wtLb} onChange={setWtLb} unit="pounds" testid="army-w"/></Row>
          <Row label="Waist"><UnitInput value={waistIn} onChange={setWaistIn} unit="inches" testid="army-waist"/></Row>
          <Row label="Neck"><UnitInput value={neckIn} onChange={setNeckIn} unit="inches" testid="army-neck"/></Row>
          {gender === 'female' && <Row label="Hip"><UnitInput value={hipIn} onChange={setHipIn} unit="inches" testid="army-hip"/></Row>}
        </> : <>
          <Row label="Height"><UnitInput value={hCm} onChange={setHCm} unit="cm" testid="army-hcm"/></Row>
          <Row label="Weight"><UnitInput value={wtKg} onChange={setWtKg} unit="kg" testid="army-wkg"/></Row>
          <Row label="Waist"><UnitInput value={waistCm} onChange={setWaistCm} unit="cm" testid="army-waistcm"/></Row>
          <Row label="Neck"><UnitInput value={neckCm} onChange={setNeckCm} unit="cm" testid="army-neckcm"/></Row>
          {gender === 'female' && <Row label="Hip"><UnitInput value={hipCm} onChange={setHipCm} unit="cm" testid="army-hipcm"/></Row>}
        </>}
      </CalcNetPanel>
    </CalcShell>
  );
}
